import { useCallback, useEffect, useRef, useState } from "preact/hooks"

import { escolherMimeType, formatarDuracao, nomeDoArquivoGravado, suportaGravacao } from "../lib/gravacao"
import { definirSom, prepararSom, somLigado } from "../lib/som"
import { Conversation } from "./Conversation"
import { Visor } from "./Visor"
import {
  IconClip,
  IconClose,
  IconMic,
  IconSairTelaCheia,
  IconSend,
  IconSound,
  IconSoundOff,
  IconStop,
  IconTelaCheia,
  IconTrash,
} from "./icons"

/**
 * O erro do servidor vira frase.
 *
 * O widget nao repete codigo cru para o visitante: `upload_413` nao diz nada a
 * quem so quer mandar uma foto. Cada caso tem a saida junto — o que fazer, e
 * nao so o que deu errado.
 */
function mensagemDoErro(bruto: string): string {
  if (bruto.includes("mime_not_allowed")) {
    return "Esse tipo de arquivo não é aceito. Envie imagem, vídeo, áudio ou PDF."
  }
  if (bruto.includes("413") || bruto.includes("too large") || bruto.includes("LIMIT_FILE_SIZE")) {
    return "Arquivo grande demais. O limite é 8 MB."
  }
  if (bruto.includes("rate_limited")) {
    return "Muitos envios seguidos. Espere um minuto e tente de novo."
  }
  if (bruto.includes("attachments_disabled")) {
    return "O envio de arquivos está desligado neste atendimento."
  }
  if (bruto.includes("media_storage_unavailable")) {
    return "Não foi possível guardar o arquivo agora. Tente mais tarde."
  }
  if (bruto.includes("audio_requires_login")) {
    // Defesa a mais: o botao de gravar ja fica fora da tela pra anonimo (ver
    // `podeGravar && !isGuest` em Window.tsx). Isto so aparece se o pedido
    // chegou ao servidor por fora da UI.
    return "Entre na sua conta para enviar áudio."
  }
  return "Não consegui enviar o arquivo. Tente de novo."
}
import type { Attendant, ChannelConfig, Entry, Status } from "../lib/types"

type Props = {
  config: ChannelConfig | null
  status: Status
  entries: Entry[]
  typing: boolean
  attendant: Attendant | null
  offline: boolean
  canSend: boolean
  /** Visitante anonimo (sem conta vinculada). Grava-e-manda audio e' so' pra quem nao e'. */
  isGuest: boolean
  blockedReason: string | null
  hasMore: boolean
  loadingMore: boolean
  onLoadMore: () => void
  onSend: (text: string, option?: { id: string; title: string } | null) => void
  /** Sobe o arquivo e manda a mensagem. Ausente = anexo desligado no canal. */
  onAttach?: ((file: File) => Promise<void>) | null
  /**
   * A conversa e o assunto da tela: tela cheia no celular, ou aberta por link
   * no desktop. Liga o Esc — em janelinha de canto, fechar por tecla seria
   * tirar a conversa da frente de quem nao pediu.
   */
  modal?: boolean
  /**
   * A janela cobre TUDO. So aqui vale `aria-modal`: no desktop a pagina atras
   * continua alcancavel (a cortina do destaque nao pega clique), e prender o
   * leitor de tela dentro do dialogo seria mentira.
   */
  telaCheia?: boolean
  /**
   * Botao de tela cheia no cabecalho. Ausente = nao cabe aqui: fora do
   * celular, ou com a tela cheia ja imposta pelo site (link, `mobileFullscreen`).
   */
  onAlternarTelaCheia?: (() => void) | null
  onClose: () => void
}

// Teto de seguranca: 10 minutos e mais que suficiente para um audio de
// atendimento e evita um arquivo gigante (e um microfone esquecido ligado).
const TETO_GRAVACAO_SEGUNDOS = 600

const STATUS_LABEL: Record<Status, string> = {
  idle: "offline",
  connecting: "conectando…",
  connected: "online agora",
  reconnecting: "reconectando…",
  unavailable: "indisponível",
}

export function Window({
  config,
  status,
  entries,
  typing,
  attendant,
  offline,
  canSend,
  isGuest,
  blockedReason,
  hasMore,
  loadingMore,
  onLoadMore,
  onSend,
  onAttach,
  modal = false,
  telaCheia = false,
  onAlternarTelaCheia,
  onClose,
}: Props) {
  const [draft, setDraft] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // Abrir o chat e ter de clicar no campo é um passo a mais sem motivo — no
    // COMPUTADOR.
    //
    // No celular é o contrário: focar sozinho sobe o teclado por cima da
    // conversa antes de a pessoa ler a primeira linha. Ela abriu para LER a
    // saudação, e recebe meia tela de teclado e a conversa espremida. Quem vai
    // escrever toca no campo — é um toque, e é o toque que ela já ia dar.
    //
    // A régua é `pointer: coarse`, e não a largura: o que importa não é a tela
    // ser pequena, é o teclado ser de vidro. Um notebook com tela sensível
    // continua com mouse (`pointer: fine`) e mantém o foco automático.
    if (window.matchMedia?.("(pointer: coarse)").matches) return

    const timer = window.setTimeout(() => inputRef.current?.focus(), 120)
    return () => window.clearTimeout(timer)
  }, [])

  const brandName = attendant?.name || config?.brand.name || "Atendimento"
  const avatarUrl = attendant?.avatarUrl || config?.brand.avatarUrl || null
  const initials = brandName.slice(0, 2).toUpperCase()

  const fileRef = useRef<HTMLInputElement>(null)
  const [enviandoAnexo, setEnviandoAnexo] = useState(false)
  const [erroAnexo, setErroAnexo] = useState<string | null>(null)
  const [som, setSom] = useState(somLigado)
  /** Imagem no visor, ou `null`. Mora aqui porque o visor cobre a janela toda. */
  const [ampliada, setAmpliada] = useState<{ url: string; mime: string | null } | null>(null)

  /**
   * Esc fecha a conversa — mas só quando ela é modal.
   *
   * Como janela no canto do site, Esc não é a saída esperada: a pessoa continua
   * na página, e fechar por tecla seria tirar a conversa da frente dela sem
   * pedir. Em tela cheia — ou com a página escurecida atrás — vale o contrário:
   * é um diálogo, e diálogo fecha no Esc.
   *
   * Com o visor de imagem aberto, este efeito NÃO registra nada. Quem fecha
   * primeiro é o visor, que tem o próprio Esc; os dois escutam no `document`, e
   * ali `stopPropagation` não segura listener nenhum — o que segura é este não
   * existir enquanto a imagem está ampliada.
   */
  useEffect(() => {
    if (!modal || ampliada) return
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") onClose()
    }
    document.addEventListener("keydown", aoTeclar)
    return () => document.removeEventListener("keydown", aoTeclar)
  }, [modal, ampliada, onClose])

  const escolherArquivo = async (event: Event) => {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    // Limpa o input SEMPRE: sem isso, escolher o mesmo arquivo duas vezes
    // seguidas nao dispara `change` na segunda.
    input.value = ""
    if (!file || !onAttach) return

    setErroAnexo(null)
    setEnviandoAnexo(true)
    try {
      await onAttach(file)
    } catch (error: any) {
      setErroAnexo(mensagemDoErro(String(error?.message || "")))
    } finally {
      setEnviandoAnexo(false)
    }
  }

  // ── gravar audio ──────────────────────────────────────────
  // Mesma ideia do anexo por arquivo: o audio gravado vira um `File` e entra
  // no MESMO `onAttach` (upload + `client.send`), sem caminho proprio.
  const [podeGravar] = useState(suportaGravacao)
  const [gravando, setGravando] = useState(false)
  const [duracaoGravacao, setDuracaoGravacao] = useState(0)
  const gravadorRef = useRef<MediaRecorder | null>(null)
  const streamGravacaoRef = useRef<MediaStream | null>(null)
  const pedacosGravacaoRef = useRef<Blob[]>([])
  const gravacaoCanceladaRef = useRef(false)
  const timerGravacaoRef = useRef<number | null>(null)

  const pararTimerGravacao = () => {
    if (timerGravacaoRef.current !== null) {
      window.clearInterval(timerGravacaoRef.current)
      timerGravacaoRef.current = null
    }
  }

  // Desmontar a janela com o microfone ainda ligado (fechar o chat no meio da
  // gravacao) nao pode deixar o navegador com o microfone aceso para sempre.
  useEffect(() => {
    return () => {
      pararTimerGravacao()
      const gravador = gravadorRef.current
      if (gravador && gravador.state !== "inactive") {
        gravacaoCanceladaRef.current = true
        gravador.stop()
      }
      streamGravacaoRef.current?.getTracks().forEach((faixa) => faixa.stop())
    }
  }, [])

  const pararGravacao = useCallback(() => {
    const gravador = gravadorRef.current
    if (!gravador || gravador.state === "inactive") return
    gravacaoCanceladaRef.current = false
    gravador.stop()
  }, [])

  const cancelarGravacao = useCallback(() => {
    const gravador = gravadorRef.current
    if (!gravador || gravador.state === "inactive") return
    gravacaoCanceladaRef.current = true
    pararTimerGravacao()
    gravador.stop()
  }, [])

  const iniciarGravacao = useCallback(async () => {
    // O botao ja fica fora da tela pra visitante anonimo (abaixo); esta e so a
    // trava de verdade, pro caso de um clique disparar antes do re-render (ou
    // de alguem chamar isto por fora do botao).
    if (gravando || !onAttach || isGuest) return
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamGravacaoRef.current = stream

      const mime = escolherMimeType((tipo) => MediaRecorder.isTypeSupported(tipo))
      const gravador = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream)
      gravadorRef.current = gravador
      pedacosGravacaoRef.current = []
      gravacaoCanceladaRef.current = false

      gravador.ondataavailable = (evento) => {
        if (evento.data.size > 0) pedacosGravacaoRef.current.push(evento.data)
      }

      gravador.onstop = () => {
        pararTimerGravacao()
        streamGravacaoRef.current?.getTracks().forEach((faixa) => faixa.stop())
        streamGravacaoRef.current = null
        setGravando(false)
        setDuracaoGravacao(0)
        gravadorRef.current = null

        const pedacos = pedacosGravacaoRef.current
        pedacosGravacaoRef.current = []
        if (gravacaoCanceladaRef.current) {
          gravacaoCanceladaRef.current = false
          return
        }

        const mimeEfetivo = gravador.mimeType || mime || "audio/webm"
        const blob = new Blob(pedacos, { type: mimeEfetivo })
        if (blob.size === 0) {
          setErroAnexo("Nenhum áudio foi capturado. Tente gravar de novo.")
          return
        }

        const arquivo = new File([blob], nomeDoArquivoGravado(mimeEfetivo), { type: mimeEfetivo })
        setErroAnexo(null)
        setEnviandoAnexo(true)
        onAttach(arquivo)
          .catch((error: any) => setErroAnexo(mensagemDoErro(String(error?.message || ""))))
          .finally(() => setEnviandoAnexo(false))
      }

      gravador.start()
      setGravando(true)
      setDuracaoGravacao(0)
      timerGravacaoRef.current = window.setInterval(() => {
        setDuracaoGravacao((anterior) => {
          const proximo = anterior + 1
          if (proximo >= TETO_GRAVACAO_SEGUNDOS) gravadorRef.current?.stop()
          return proximo
        })
      }, 1000)
    } catch {
      streamGravacaoRef.current?.getTracks().forEach((faixa) => faixa.stop())
      streamGravacaoRef.current = null
      setGravando(false)
      setErroAnexo("Não foi possível acessar o microfone.")
    }
  }, [gravando, onAttach, isGuest])

  const submit = (event: Event) => {
    event.preventDefault()
    // Mandar mensagem tambem e gesto: serve para liberar o audio quando a
    // conversa ja abriu sozinha (modo standalone, ou reaberta ao carregar).
    prepararSom()
    const text = draft.trim()
    if (!text || !canSend) return
    onSend(text)
    setDraft("")
  }

  return (
    <section
      class="window"
      role="dialog"
      aria-modal={telaCheia ? "true" : undefined}
      aria-label={`Conversa com ${brandName}`}
    >
      <header class="header">
        {avatarUrl ? (
          <img class="avatar" src={avatarUrl} alt="" />
        ) : (
          <div class="avatar" aria-hidden="true">
            {initials}
          </div>
        )}
        <div>
          <div class="title">{brandName}</div>
          <div class="status">
            <span class="dot" aria-hidden="true" />
            <span>{attendant ? "atendente" : STATUS_LABEL[status]}</span>
          </div>
        </div>
        <div class="header-acoes">
        <button
          type="button"
          class="icon-btn"
          onClick={() => {
            const proximo = !som
            definirSom(proximo)
            setSom(proximo)
            if (proximo) prepararSom()
          }}
          aria-label={som ? "Desligar aviso sonoro" : "Ligar aviso sonoro"}
          title={som ? "Desligar aviso sonoro" : "Ligar aviso sonoro"}
          aria-pressed={!som}
        >
          {som ? <IconSound /> : <IconSoundOff />}
        </button>
        {onAlternarTelaCheia && (
          <button
            type="button"
            class="icon-btn"
            onClick={onAlternarTelaCheia}
            aria-label={telaCheia ? "Sair da tela cheia" : "Tela cheia"}
            title={telaCheia ? "Sair da tela cheia" : "Tela cheia"}
            aria-pressed={telaCheia}
          >
            {telaCheia ? <IconSairTelaCheia /> : <IconTelaCheia />}
          </button>
        )}
        <button
          type="button"
          class="icon-btn"
          onClick={onClose}
          aria-label="Fechar conversa"
        >
          <IconClose />
        </button>
        </div>
      </header>

      {attendant && (
        <div class="banner info">
          {attendant.name
            ? `${attendant.name} entrou na conversa.`
            : "Um atendente entrou na conversa."}
        </div>
      )}

      {!attendant && offline && (
        // Avisa, mas o campo continua liberado: a mensagem fica na fila.
        // Bloquear aqui transforma "estamos fechados" em "vá embora".
        <div class="banner">
          Estamos fora do horário de atendimento. Pode escrever — respondemos na
          abertura.
        </div>
      )}

      {status === "unavailable" && (
        <div class="banner">
          Não foi possível abrir a conversa agora. Recarregue a página em alguns
          instantes.
        </div>
      )}

      <Conversation
        entries={entries}
        typing={typing}
        hasMore={hasMore}
        loadingMore={loadingMore}
        onLoadMore={onLoadMore}
        onOption={(titulo, opcao) => onSend(titulo, opcao)}
        onAmpliar={(url, mime) => setAmpliada({ url, mime })}
      />

      {ampliada && (
        <Visor
          url={ampliada.url}
          mime={ampliada.mime}
          onFechar={() => setAmpliada(null)}
        />
      )}

      {erroAnexo && (
        <div class="banner" role="status">
          {erroAnexo}
        </div>
      )}

      <form class="composer" onSubmit={submit}>
        <label class="sr-only" for="chat-input">
          Sua mensagem
        </label>

        {gravando ? (
          <>
            <button
              type="button"
              class="attach"
              onClick={cancelarGravacao}
              aria-label="Cancelar gravação"
              title="Cancelar gravação"
            >
              <IconTrash />
            </button>
            <div class="gravando" role="status" aria-live="polite">
              <span class="gravando-ponto" aria-hidden="true" />
              <span class="gravando-tempo">{formatarDuracao(duracaoGravacao)}</span>
              <span class="gravando-rotulo">Gravando áudio…</span>
            </div>
            <button
              type="button"
              class="send"
              onClick={pararGravacao}
              aria-label="Parar e enviar áudio"
              title="Parar e enviar"
            >
              <IconStop />
            </button>
          </>
        ) : (
          <>
            {onAttach && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  class="sr-only"
                  onChange={escolherArquivo}
                  accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,audio/mpeg,audio/ogg,audio/webm,audio/wav,application/pdf"
                />
                <button
                  type="button"
                  class="attach"
                  disabled={!canSend || status !== "connected" || enviandoAnexo}
                  aria-label={enviandoAnexo ? "Enviando anexo" : "Anexar arquivo"}
                  onClick={() => fileRef.current?.click()}
                >
                  {enviandoAnexo ? <span class="attach-spin" aria-hidden="true" /> : <IconClip />}
                </button>
              </>
            )}
            {/* So' pra quem a conversa ja' amarrou a uma conta — anonimo manda
                texto e anexo comum (o `onAttach` acima), igual sempre mandou;
                audio pedido pelo microfone fica reservado pra depois do login. */}
            {onAttach && podeGravar && !isGuest && (
              <button
                type="button"
                class="attach"
                disabled={!canSend || status !== "connected" || enviandoAnexo}
                aria-label="Gravar áudio"
                title="Gravar áudio"
                onClick={iniciarGravacao}
              >
                <IconMic />
              </button>
            )}
            <input
              id="chat-input"
              ref={inputRef}
              type="text"
              autocomplete="off"
              value={draft}
              disabled={!canSend}
              placeholder={
                blockedReason ??
                (status === "connected"
                  ? "Escreva sua mensagem"
                  : status === "connecting" || status === "idle"
                    ? "Conectando…"
                    : // "Reconectando…" com a conversa indisponivel e mentira: ja
                      // paramos de tentar. O aviso acima explica; aqui basta nao
                      // prometer o que nao vai acontecer.
                      status === "unavailable"
                      ? "Indisponível no momento"
                      : "Reconectando…")
              }
              onInput={(event) => setDraft((event.target as HTMLInputElement).value)}
            />
            <button
              type="submit"
              class="send"
              disabled={!canSend || !draft.trim()}
              aria-label="Enviar mensagem"
            >
              <IconSend />
            </button>
          </>
        )}
      </form>
    </section>
  )
}
